import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import appConfig from 'config/app.config';
import apiHelper from 'helpers/api-helper';
import {getText, renderMarkdown} from 'helpers/text-helper';
import {themesData} from 'data/themes-data';
import LanguageSelectDropdown from 'components/ui/language-select-dropdown/language-select-dropdown';
import Button from 'components/ui/button/button';
import './survey.scss';

const Survey = ({deviceInfo}) => {
	const [languageId, setLanguageId] = useState('da');

	const [isLoadingGameData, setIsLoadingGameData] = useState(true);
	const [isCompleted, setIsCompleted] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const [gameData, setGameData] = useState(null);
	const [questionIndex, setQuestionIndex] = useState(0);
	const [questionAnswers, setQuestionAnswers] = useState([]);

	/* Game step data */
	let gameStepData = null;
	if (gameData) {
		const themeData = (gameData.themeId 
			? themesData.find((t) => {return t.id === gameData.themeId;})
			: themesData[0]
		);
		const gameStepIndex = (themeData
			? themeData.gameSteps.findIndex((step) => {return step.type === 'survey';})
			: -1
		);
		if (gameStepIndex >= 0) {
			gameStepData = themeData.gameSteps[gameStepIndex];
		}
	}

	/* Current question data */
	const questionData = (!isCompleted && gameStepData && gameStepData.questions.length > questionIndex
		? gameStepData.questions[questionIndex]
		: null
	);

	let canConfirmSelectedOptions = false;
	if (questionData) {
		const questionAnswer = questionAnswers.find((answer) => {return answer.questionId === questionData.id;});
		if (questionAnswer && questionAnswer.selectedOptionIds) {
			canConfirmSelectedOptions = (
				(
					questionData.numberOfAnswers && 
					questionAnswer.selectedOptionIds.length === questionData.numberOfAnswers
				) ||
				(
					questionData.maxNumberOfAnswers && 
					questionAnswer.selectedOptionIds.length > 0 && 
					questionAnswer.selectedOptionIds.length <= questionData.maxNumberOfAnswers
				)
			);
		}
	}

	/**
	 * Select option
	 * @param {string} optionId 
	 */
	const selectOption = (optionId) => {
		const newQuestionAnswers = JSON.parse(JSON.stringify(questionAnswers));
		const questionIndex = newQuestionAnswers.findIndex((question) => {
			return question.questionId === questionData.id;
		});
		if (questionIndex >= 0) {
			const selectedOptionIds = JSON.parse(JSON.stringify(newQuestionAnswers[questionIndex].selectedOptionIds));
			const optionIndex = selectedOptionIds.indexOf(optionId);
			if (optionIndex >= 0) {
				/* De-select option */
				selectedOptionIds.splice(optionIndex, 1);
			} else {
				/* Select option */
				if (
					(questionData.numberOfAnswers && questionData.numberOfAnswers > selectedOptionIds.length) ||
					(questionData.maxNumberOfAnswers && questionData.maxNumberOfAnswers > selectedOptionIds.length)
				) {
					selectedOptionIds.push(optionId);
				}
			}
			newQuestionAnswers[questionIndex].selectedOptionIds = selectedOptionIds;
		} else {
			newQuestionAnswers.push({questionId: questionData.id, selectedOptionIds: [optionId]});
		}
		setQuestionAnswers(newQuestionAnswers);
	};

	/**
	 * Confirm selected options
	 */
	const confirmSelectedOptions = () => {
		if (isLoading) return;

		setIsLoading(true);
		const selectedOptionIds = JSON.parse(JSON.stringify(questionAnswers[questionIndex].selectedOptionIds));
		const savedQuestionIndex = questionAnswers[questionIndex].hasOwnProperty('savedQuestionIndex') 
			? questionAnswers[questionIndex].savedQuestionIndex 
			: -1;
		apiHelper('group/answer-survey-question', {
			gameId: gameData.id, 
			gameStepId: gameStepData.id, 
			questionId: questionData.id, 
			optionIds: selectedOptionIds, 
			savedQuestionIndex
		}).then((response)=>{
			if (response.status === 'success') {
				questionAnswers[questionIndex].savedQuestionIndex = response.savedQuestionIndex;
				if (questionIndex < gameStepData.questions.length - 1) {
					/* Go to next question */
					setQuestionIndex(questionIndex + 1);
					setIsLoading(false);
				} else {
					setIsCompleted(true);
					setIsLoading(false);
				}
			} else {
				setIsLoading(false);	
			}
		}).catch((error) => {
			console.error(error);
			setIsLoading(false);
		});
	};

	/**
	 * Find the game based on the url
	 * @param {string} gameCode 
	 */
	const findGame = (gameCode) => {
		return new Promise((resolve) => {
			const db = firebase.firestore();
			/* Get game */
			db.collection(appConfig.gamesDbName).doc(gameCode).get().then((doc) => {
				if (doc.exists && doc.data()) {
					/* Game */
					const game = {...doc.data(), id: doc.id};

					/* Get groups of game */
					db.collection(appConfig.groupsDbName).where('gameId', '==', doc.id).get().then((docs) => {
						const groups = [];

						docs.forEach((doc) => {groups.push({id: doc.id, ...doc.data()});});
						resolve({status: 'success', game, groups});
					}).catch((error) => {
						resolve({status: 'error', error: error});	
					});
				} else {
					resolve({status: 'error'});
				}
			}).catch((error) => {
				resolve({status: 'error', error: error});	
			});	
		});	
	};

	const goBack = () => {
		if (isCompleted) {
			setIsCompleted(false);
		} else {
			setQuestionIndex(questionIndex - 1);
		}
	};

	/**
	 * Component did mount
	 */
	useEffect(() => {
		const pathName = window.location.pathname.toLowerCase();
		if (pathName.length > 0) {
			const pathNameArr = pathName.split('/');
			if (pathNameArr.length === 3 && pathNameArr[1] === 'survey' && pathNameArr[2].length > 0) {
				const gameCode = pathNameArr[2];
				findGame(gameCode).then((response) => {
					if (response.status === 'success') {
						setGameData(response.game);
					}
					setIsLoadingGameData(false);
				});

			} else {
				setIsLoadingGameData(false);
			}
		} else {
			setIsLoadingGameData(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className={'Survey ' + deviceInfo.orientation + (isCompleted ? ' completed' : '')}>
		
			<div className="Survey-groupImage" />

			{!isCompleted && <div className="Survey-selectLanguage">
				<LanguageSelectDropdown 
					languageId={languageId}
					layout="player"
					deviceInfo={deviceInfo}
					handleSelectLanguage={(newLanguageId) => {setLanguageId(newLanguageId);}}
				/>
			</div>}

			{/* Survey step page */}
			<div className="Survey-content">

				{/* No game at this URL */}
				{(!isLoadingGameData && !gameStepData) && <div className="Survey-noGame">
					<span>{getText('errors_gameDoesNotExist', languageId)}</span>
				</div>}

				{(isCompleted && gameStepData) && <div className="Survey-finished">
					<div className='Survey-finishedTitle'>
						<span>{getText(gameStepData.finishedTitle, languageId)}</span>
					</div>
					<div className='Survey-finishedText'>
						<span>{getText(gameStepData.finishedText, languageId)}</span>
					</div>	
				</div>}

				{/* Question */}
				{questionData && <div className="Survey-question">
					{questionData.title && <div className="Survey-questionTitle">
						<span>{getText(questionData.title, languageId)}</span>
					</div>}
					{questionData.text && <div className="Survey-questionText">
						{renderMarkdown(getText(questionData.text, languageId))}
					</div>}
					<div className="Survey-options">
						{questionData.options.map((option) => {
							const isSelected = questionAnswers.some((q) => {
								return q.questionId === questionData.id && q.selectedOptionIds.includes(option.id);
							});
							return (
								<div 
									key={option.id} 
									className={'Survey-option' + (isSelected ? ' selected' : '')}
									onClick={() => {selectOption(option.id);}}
								>
									<span>{getText(option.text, languageId)}</span>
								</div>
							);
						})}
					</div>
				</div>}
			</div>

			{/* Survey footer */}
			{gameData && <div className="Survey-footer">
				{/* Button: confirm selected options / next step */}
				{!isCompleted && <div className="Survey-nextBtn">
					<Button 
						classes={['orange', 'next', 'fullHeight']}
						isLoading={isLoading}
						isDisabled={isLoading || !canConfirmSelectedOptions}
						text={getText('general_continue', languageId)}
						onClick={() => {confirmSelectedOptions();}}
					/>
				</div>}
				{/* Button: prev step */}
				{questionIndex > 0 && <div className="Survey-prevBtn">
					<Button 
						classes={['orange', 'fullHeight']}
						text={getText('general_back', languageId)}
						onClick={() => {goBack();}}
					/>
				</div>}


			</div>}
		</div>
	);
};

Survey.propTypes = {
	deviceInfo: PropTypes.object.isRequired
};

export default Survey;